import React, { useEffect, useState } from "react";
import { api, formatApiError, BACKEND_URL } from "../../lib/api";
import { ContactIcons, MessageComposeDialog } from "../../components/admin/ContactPanel";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle2, XCircle, FileImage, ShieldCheck } from "lucide-react";

const DOC_LABELS = {
  id: "National ID / Passport",
  driver_license: "Driver's license",
  selfie: "Selfie with ID",
  proof_of_address: "Proof of address",
  national_id: "National ID",
  passport: "Passport",
  driving_licence: "Driving Licence",
};

export default function AdminKycPanel() {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectOpen, setRejectOpen] = useState(false);
  const [working, setWorking] = useState(false);
  const [messageTarget, setMessageTarget] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/kyc/pending");
      setUsers(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openReview = async (u) => {
    try {
      const { data } = await api.get(`/admin/kyc/${u.id}`);
      setSelected(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const approve = async () => {
    if (!selected) return;
    setWorking(true);
    try {
      await api.post(`/admin/kyc/${selected.id}/approve`);
      toast.success("Identity verified");
      setSelected(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setWorking(false); }
  };

  const reject = async () => {
    if (!selected || rejectReason.trim().length < 3) {
      toast.error("Please provide a reason (3+ chars)");
      return;
    }
    setWorking(true);
    try {
      await api.post(`/admin/kyc/${selected.id}/reject`, { reason: rejectReason });
      toast.success("Submission rejected — user notified");
      setRejectOpen(false);
      setRejectReason("");
      setSelected(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setWorking(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-kyc-panel">
      <Card className="border-border p-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="font-display text-xl font-semibold tracking-tight">KYC review queue</h3>
            <p className="text-sm text-muted-foreground">{users.length} submission{users.length === 1 ? "" : "s"} awaiting review</p>
          </div>
          <Button variant="outline" onClick={load} data-testid="kyc-refresh-btn">Refresh</Button>
        </div>
        {users.length === 0 ? (
          <div className="border border-dashed border-border rounded-md p-10 text-center">
            <ShieldCheck className="mx-auto text-muted-foreground mb-2" size={28} />
            <div className="font-display text-foreground mb-1">All caught up</div>
            <p className="text-sm text-muted-foreground">No pending KYC submissions right now.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="kyc-queue-list">
            {users.map((u) => (
              <div key={u.id} className="flex items-center gap-3 border-b border-border last:border-b-0 pb-3 last:pb-0" data-testid={`kyc-queue-${u.id}`}>
                <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-display font-bold">
                  {u.name?.charAt(0)?.toUpperCase() || "U"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold">{u.name}</div>
                  <div className="text-xs text-muted-foreground">{u.email} · <span className="capitalize">{u.role}</span> · submitted {u.kyc_submitted_at?.split("T")[0]}</div>
                  <ContactIcons email={u.email} phone={u.phone} userId={u.id} name={u.name} isGuest={false} setMessageTarget={setMessageTarget} />
                </div>
                {(u.kyc_documents || []).length > 0 ? (
                  <Badge variant="outline" className="capitalize">{u.kyc_documents.length} docs</Badge>
                ) : u.kyc_document_type ? (
                  <Badge variant="outline" className="capitalize">{DOC_LABELS[u.kyc_document_type] || u.kyc_document_type}</Badge>
                ) : null}
                {(u.kyc_ocr_flags || []).length > 0 && (
                  <Badge variant="destructive">OCR flagged</Badge>
                )}
                <Button size="sm" onClick={() => openReview(u)} className="bg-primary hover:bg-primary/90" data-testid={`kyc-review-${u.id}`}>Review</Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Dialog open={Boolean(selected)} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">{selected.name} — KYC review</DialogTitle>
                <div className="text-sm text-muted-foreground">{selected.email} · {selected.phone || "no phone"} · <span className="capitalize">{selected.role}</span></div>
                <ContactIcons email={selected.email} phone={selected.phone} userId={selected.id} name={selected.name} isGuest={false} setMessageTarget={setMessageTarget} />
              </DialogHeader>

              {(selected.kyc_ocr_flags || []).length > 0 && (
                <div className="mt-4 border border-red-200 bg-red-50 rounded-md p-3 space-y-1">
                  {selected.kyc_ocr_flags.map((flag, i) => (
                    <div key={i} className="text-xs text-red-700">⚠ {flag}</div>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 max-h-[60vh] overflow-y-auto">
                {(selected.kyc_documents || []).map((d) => {
                  const isPdf = d.file_path?.toLowerCase().endsWith(".pdf");
                  const docUrl = `${BACKEND_URL}/api/admin/kyc/${selected.id}/document`;
                  return (
                    <div key={d.id} className="border border-border rounded-md overflow-hidden" data-testid={`kyc-review-doc-${d.type}`}>
                      <div className="aspect-video bg-muted flex items-center justify-center">
                        {isPdf ? (
                          <a href={docUrl} target="_blank" rel="noreferrer" className="flex flex-col items-center text-primary">
                            <FileImage size={36} />
                            <span className="text-xs mt-1 underline">Open PDF</span>
                          </a>
                        ) : (
                          <img src={docUrl} alt={d.type} className="w-full h-full object-contain" />
                        )}
                      </div>
                      <div className="p-3">
                        <div className="font-display font-semibold text-sm">{DOC_LABELS[d.type] || d.type}</div>
                        <div className="text-xs text-muted-foreground">Uploaded {d.uploaded_at?.split("T")[0]}</div>
                        <a href={docUrl} target="_blank" rel="noreferrer" className="text-xs text-primary underline mt-1 inline-block">Open original</a>
                      </div>
                    </div>
                  );
                })}
                {(selected.kyc_documents || []).length === 0 && selected.kyc_document_path && (() => {
                  const isPdf = selected.kyc_document_path?.toLowerCase().endsWith(".pdf");
                  return (
                    <div className="border border-border rounded-md overflow-hidden" data-testid="kyc-review-doc-single">
                      <div className="aspect-video bg-muted flex items-center justify-center">
                        {isPdf ? (
                          <a href={`${BACKEND_URL}/api/admin/kyc/${selected.id}/document`} target="_blank" rel="noreferrer" className="flex flex-col items-center text-primary">
                            <FileImage size={36} />
                            <span className="text-xs mt-1 underline">Open PDF</span>
                          </a>
                        ) : (
                          <img src={`${BACKEND_URL}/api/admin/kyc/${selected.id}/document`} alt={selected.kyc_document_type} className="w-full h-full object-contain" />
                        )}
                      </div>
                      <div className="p-3">
                        <div className="font-display font-semibold text-sm">{DOC_LABELS[selected.kyc_document_type] || selected.kyc_document_type}</div>
                        <div className="text-xs text-muted-foreground">Uploaded {selected.kyc_submitted_at?.split("T")[0]}</div>
                        <a href={`${BACKEND_URL}/api/admin/kyc/${selected.id}/document`} target="_blank" rel="noreferrer" className="text-xs text-primary underline mt-1 inline-block">Open original</a>
                      </div>
                    </div>
                  );
                })()}
              </div>

              <DialogFooter className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                <Button variant="outline" onClick={() => setSelected(null)} data-testid="kyc-cancel-btn">Cancel</Button>
                <Button variant="destructive" onClick={() => setRejectOpen(true)} disabled={working} data-testid="kyc-reject-btn">
                  <XCircle size={14} className="mr-1.5" /> Reject
                </Button>
                <Button onClick={approve} disabled={working} className="bg-primary hover:bg-primary/90" data-testid="kyc-approve-btn">
                  <CheckCircle2 size={14} className="mr-1.5" /> Approve & verify
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Reject KYC submission</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">The reason below will be emailed to the user so they can re-submit. Be clear and respectful.</p>
            <Textarea
              rows={4}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. The driver's license image is blurry. Please upload a sharper photo with all four corners visible."
              data-testid="kyc-reject-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={reject} disabled={working} data-testid="kyc-reject-confirm-btn">Send rejection</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <MessageComposeDialog messageTarget={messageTarget} setMessageTarget={setMessageTarget} />
    </div>
  );
}
